package fr.frux.shared.domain.repository

import fr.frux.shared.domain.model.User

/**
 * Repository interface for authentication operations (ADR 0012, passwordless OTP email).
 */
interface AuthRepository {
    /**
     * Demande l'envoi d'un code à [email]. N'établit pas de session : la réponse ne distingue pas
     * une adresse connue d'une adresse inconnue (ADR 0012).
     *
     * [purpose] borne ce que le code prouvera à la vérification (#274) : `null` (défaut) pour se
     * connecter, `"DELETE_ACCOUNT"` pour confirmer une suppression de compte — cf.
     * [deleteAccount]. Un code de connexion ne peut pas confirmer une suppression, et
     * inversement.
     *
     * La valeur de succès est le code échoé par le serveur quand il tourne en mode dev
     * (fournisseur d'email `log`, cf. `RequestCodeResponse.devCode`) — toujours null en
     * production. Ne sert qu'au confort de dev/test : préremplir le champ code.
     */
    suspend fun requestCode(
        email: String,
        purpose: String? = null,
    ): Result<String?>

    /**
     * Vérifie [code] pour [email]. [username] n'est fourni que lors d'une seconde soumission,
     * après un [VerifyOutcome.NeedsUsername] — c'est le même code, pas un nouveau.
     *
     * Stocke le JWT et renvoie [VerifyOutcome.Success] en cas de succès. Les autres issues
     * métier (code faux, pseudo pris ou invalide, pseudo requis) sont des [Result.success] portant
     * la variante correspondante : ce ne sont pas des échecs de transport, l'appelant doit les
     * distinguer sans avoir à inspecter une exception. Seuls les échecs réseau/serveur/inattendus
     * remontent en [Result.failure].
     */
    suspend fun verifyCode(
        email: String,
        code: String,
        username: String? = null,
    ): Result<VerifyOutcome>

    /**
     * Get current logged-in user.
     * Returns null if not authenticated.
     */
    suspend fun getCurrentUser(): Result<User?>

    /**
     * Dernier compte confirmé par le serveur, conservé localement pour servir l'identité hors
     * ligne (#657). `null` sans session ou après toute sortie de session. Ne prouve rien côté
     * serveur : le jeton reste la seule autorité, cette fiche ne sert qu'à afficher qui est
     * connecté quand [getCurrentUser] ne peut pas joindre le serveur.
     */
    suspend fun lastKnownUser(): User?

    /**
     * Logout current user.
     * Clears JWT token.
     */
    suspend fun logout(): Result<Unit>

    /**
     * Check if user is authenticated (has valid token).
     */
    suspend fun isAuthenticated(): Boolean

    /**
     * « Déconnecter tous les appareils » (#26, ADR 0019) : révoque toutes les familles de
     * sessions de l'utilisateur courant, celle-ci comprise, puis purge les credentials locaux —
     * seulement si le serveur a confirmé la révocation. Un échec réseau laisse volontairement la
     * session locale intacte : mieux vaut rester connecté ici que se déconnecter que sur cet
     * appareil sans que les autres l'aient réellement été.
     */
    suspend fun logoutAll(): Result<Unit>

    /**
     * Efface définitivement le compte courant, reconfirmé par [code] — le même code OTP que la
     * connexion, obtenu via [requestCode] (#194, #172, ADR 0012). Un code faux ou expiré (401) est
     * reclassé en [DeleteAccountOutcome.InvalidCode] plutôt que remonté en échec de transport,
     * sur le modèle de [VerifyOutcome] pour [verifyCode] : c'est la même question qu'à la
     * connexion, une issue métier normale de ce formulaire, pas une session morte.
     */
    suspend fun deleteAccount(code: String): Result<DeleteAccountOutcome>

    /**
     * Export de portabilité du compte courant (#194, #172, RGPD art. 20), rendu en JSON texte
     * plutôt qu'en modèle de domaine structuré : rien ici n'est interprété par le domaine, la
     * seule opération est de le proposer tel quel au téléchargement. Faire transiter un type de
     * domaine parallèle à `AccountExportDto` dupliquerait le contrat sans bénéfice.
     */
    suspend fun exportAccount(): Result<String>
}

/** Issue métier d'une vérification de code (ADR 0012). */
sealed interface VerifyOutcome {
    /** Code accepté : la session est ouverte, [user] est le compte authentifié. */
    data class Success(
        val user: User,
    ) : VerifyOutcome

    /** L'adresse n'a pas encore de compte : re-soumettre le même code avec un pseudo. */
    data object NeedsUsername : VerifyOutcome

    /** Code faux, expiré ou déjà consommé (401 `INVALID_CODE`). */
    data object InvalidCode : VerifyOutcome

    /** Le pseudo choisi est déjà pris par un autre compte (409 `USERNAME_EXISTS`). */
    data object UsernameTaken : VerifyOutcome

    /** Le pseudo choisi ne respecte pas `IdentityPolicy` (400 `INVALID_USERNAME`). */
    data object UsernameInvalid : VerifyOutcome
}

/** Issue métier d'une tentative d'effacement de compte (#194, #172). */
sealed interface DeleteAccountOutcome {
    /** Compte effacé, jeton serveur invalidé. */
    data object Deleted : DeleteAccountOutcome

    /** Code faux, expiré ou déjà consommé (401 `INVALID_CODE`) — le compte est intact. */
    data object InvalidCode : DeleteAccountOutcome
}
